import { useContext, useEffect, useState } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { useNavigate, useParams } from "react-router-dom";
import { useValidation } from "../../hooks/useValidation";
import { createCustomerSchema } from "../../validation/customerSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "../../services/toastService";
import { customerService } from "../../services/customerService";
import { cityService } from "../../services/cityService";
import Loading from "../../components/common/Loading";
import BackButton from "../../components/common/BackButton";
import { urlConfig } from "../../config/baseUrl";

const CustomerForm = () => {
  const { t, lang } = useContext(LocaleContext);
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [isFilePicked, setIsFilePicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const initialForm = {
    fullName: "",
    phoneNumberCode: "+966",
    phoneNumber: "",
    email: "",
    age: "",
    gender: "",
    cityId: "",
    file: null,
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await getAllCities();
    if (isEditMode) await editAsync();
    setLoading(false);
  };

  const getAllCities = async () => {
    var data = await cityService.getDropdownAsync();
    setCities(data);
  };

  const handleClear = () => {
    reset(initialForm);
  };

  const v = useValidation();
  const schema = createCustomerSchema(v, t, isEditMode);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialForm,
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "file") {
          formData.append(key, value);
        }
      });
      if (values.file) {
        formData.append("file", values.file);
      }
      if (isEditMode) {
        await updateAsync(formData);
      } else {
        await addAsync(formData);
      }
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    }
  };

  const addAsync = async (formData) => {
    try {
      setBtnLoading(true);
      const response = await customerService.create(formData);
      if (!response.isSuccess) {
        response.code === 2
          ? toast.error(t("thisPhoneNumberAlreadyExists"))
          : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        console.log(response);
        return;
      }
      toast.success(t("recordAddedSuccessfully"));
      navigate("/customers");
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const editAsync = async () => {
    const response = await customerService.edit(id);
    if (!response.isSuccess) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      return;
    }
    const data = response.value;
    console.log(data);
    setPreviewImage(data.imagePath);
    reset({
      fullName: data.fullName,
      phoneNumberCode: data.phoneNumberCode,
      phoneNumber: data.phoneNumber,
      email: data.email,
      age: data.age ?? "",
      gender: data.gender?.toLowerCase() ?? "",
      cityId: data.cityId,
      file: null,
    });
  };

  const updateAsync = async (formData) => {
    try {
      setBtnLoading(true);
      const response = await customerService.update(id, formData);
      if (!response.isSuccess) {
        response.code === 2
          ? toast.error(t("thisPhoneNumberAlreadyExistsForAnotherUser"))
          : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        console.log(response);
        return;
      }
      toast.success(t("recordUpdatedSuccessfully"));
      navigate("/customers");
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const deleteImage = async () => {
    try {
      setLoading(true);
      const response = await customerService.deleteImageAsync(id);
      if (!response.isSuccess) {
        toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        return;
      }
      toast.success(t("recordDeletedSuccessfully"));
      setPreviewImage(null);
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;
  return (
    <section className="client-form">
      <div className="default-heading">
        {t("Customers")}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-card">
          <div className="my-card-header d-flex justify-content-between align-items-center">
            {isEditMode ? (
              <>
                <i className="fas fa-edit"></i> {t("editOrSelectedNarrator")}
              </>
            ) : (
              <>
                <i className="fas fa-plus plus-icon"></i> {t("addNewClient")}
              </>
            )}
            <BackButton />
          </div>
          <div className="my-card">
            <div className="my-card-header text-primary">
              {t("basicInformation")}
            </div>
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("fullName")}</label>
                  <input
                    type="text"
                    {...register("fullName")}
                    className="form-control"
                    placeholder={t("fullName")}
                  />
                  {errors.fullName && (
                    <p className="text-danger">{errors.fullName.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("phoneNumber")}</label>
                  <input
                    type="text"
                    {...register("phoneNumber")}
                    className="form-control"
                    placeholder={t("phoneNumber")}
                  />
                  {errors.phoneNumber && (
                    <p className="text-danger">{errors.phoneNumber.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("email")}</label>
                  <input
                    type="text"
                    {...register("email")}
                    className="form-control"
                    placeholder={t("email")}
                  />
                  {errors.email && (
                    <p className="text-danger">{errors.email.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("age")}</label>
                  <input
                    type="number"
                    {...register("age")}
                    className="form-control"
                    placeholder={t("age")}
                    min={1}
                    max={120}
                  />
                  {errors.age && (
                    <p className="text-danger">{errors.age.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("gender")}</label>
                  <select className="form-control" {...register("gender")}>
                    <option value="">{t("select")}</option>
                    <option value="male">{t("male")}</option>
                    <option value="female">{t("female")}</option>
                  </select>
                  {errors.gender && (
                    <p className="text-danger">{errors.gender.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("accountInformation")}
            </div>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("city")}</label>
                  <select className="form-control" {...register("cityId")}>
                    <option value="">{t("selectCity")}</option>
                    {cities?.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name[lang]}
                      </option>
                    ))}
                  </select>
                  {errors.cityId && (
                    <p className="text-danger">{errors.cityId.message}</p>
                  )}
                </div>
              </div>
              {!previewImage ? (
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("profilePicture")}</label>
                    <input
                      type="file"
                      id="profile-picture"
                      className="d-none"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setValue("file", file);
                          setIsFilePicked(true);
                        }
                      }}
                    />
                    <label htmlFor="profile-picture" className="my-file-upload">
                      <label htmlFor="profile-picture" className="cursor-pointer">
                        {t("dragAndDropTheImageHereOr")}{" "}
                        <span className="text-primary">
                          {t("browseImages")} {isFilePicked && "(1)"}
                        </span>
                      </label>
                      <i className="fas fa-camera" />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <div className="form-group">
                    <div className="preview-images">
                      <div className="preview-image">
                        <img
                          src={`${urlConfig.imageBaseUrl}/${previewImage}`}
                        />
                        <i
                          className="fas fa-times"
                          onClick={() => deleteImage()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 gap-2 d-flex justify-content-end">
            {!btnLoading && (
              <button type="submit" className="btn-default">
                {t("save")}
              </button>
            )}
            {btnLoading && (
              <button type="button" className="btn-default">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            )}
            {isEditMode && (
              <button type="button" className="btn-secondary" onClick={handleClear}>
                {t("reset")}
              </button>
            )}
            {!isEditMode && (
              <button type="button" className="btn-secondary" onClick={handleClear}>
                {t("reset")}
              </button>
            )}
          </div>
        </div>
      </form>
    </section>
  );
};

export default CustomerForm;
